import React, { useState, useEffect, useContext, useMemo } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom"; 
import { useTranslation } from "../context/LanguageContext";
import { UserContext } from "../context/UserContext";
import { FaPlus, FaBuilding, FaEdit, FaEye } from "react-icons/fa";
import { DataGridTable, SearchBar } from "../components";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import "../styles/common.css";
import "./Ranches.css";

export default function Ranches() {
  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  const [ranches, setRanches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // État pour le zoom (gardé pour les colonnes qui utilisent zoomLevel dans leurs styles)
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('ranches-zoom-level');
    return saved ? parseFloat(saved) : 1.0;
  });

  useEffect(() => {
    localStorage.setItem('ranches-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchRanches = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        
        const response = await api.get("/api/ranches/", { 
          params,
          signal: abortController.signal 
        }); 
        setRanches(response.data);
        setError(null);
      } catch (err) {
        // Ne pas mettre à jour l'état si la requête a été annulée
        // Axios avec AbortController lance une CanceledError quand la requête est annulée
        const isAborted = err.name === 'CanceledError' || 
                         err.code === 'ERR_CANCELED' || 
                         err.message === 'canceled' ||
                         abortController.signal.aborted;
        if (isAborted) {
          return;
        }
        setError("Impossible de charger les données des ranchs.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchRanches();
    
    // Nettoyage : annuler la requête en cours si le composant est démonté ou si les dépendances changent
    return () => {
      abortController.abort();
    };
  }, [searchTerm]);


  // Configuration des colonnes pour DataGrid
  const baseColumns = useMemo(() => [
    {
      field: 'nom',
      headerName: 'Nom',
      width: 200,
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'code',
      headerName: 'Code',
      width: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'localisation',
      headerName: 'Localisation',
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'telephone',
      headerName: 'Téléphone',
      width: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'contact_personne',
      headerName: 'Contact',
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'actif',
      headerName: 'Statut',
      width: 130,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <span 
          className={params.value ? 'status-actif' : 'status-inactif'}
          style={{ fontSize: `${1.3 * zoomLevel}rem` }}
        >
          {params.value ? 'Actif' : 'Inactif'}
        </span>
      ),
    },
  ], [zoomLevel]);

  const adminColumns = useMemo(() => {
    const canEdit = user?.is_admin || hasPermission(user, PERMISSIONS.CHANGE_RANCHES);
    if (!canEdit) return [];
    
    return [
      {
        field: 'actions',
        headerName: 'Actions',
        width: 130,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <button
            onClick={() => navigate(`/ranches/${params.row.id}/modifier`)}
            className="grid-action-button"
            style={{ fontSize: `${1.3 * zoomLevel}rem` }}
          >
            Modifier
          </button>
        ),
      },
    ];
  }, [user, navigate, zoomLevel]);

  const columns = useMemo(() => {
    return adminColumns.length > 0 ? [...baseColumns, ...adminColumns] : baseColumns;
  }, [baseColumns, adminColumns]);

  if (loading) {
    return (
      <div className="ranches-page">
        <div className="loading-container">
          <p className="loading-text">{t.loadingRanches}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ranches-page">
        <div className="title-badge">
          <FaBuilding className="page-title-icon" aria-hidden="true" />
          <span>{t.error}</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ranches-page">
      <div className="title-badge">
        <FaBuilding className="page-title-icon" aria-hidden="true" />
        <span>{t.ranchList}</span>
      </div>

      {/* Bouton d'action admin */}
      {(user?.is_admin || hasPermission(user, PERMISSIONS.ADD_RANCHES)) && (
        <div className="action-buttons-container">
          <button
            type="button"
            className="save-but"
            onClick={() => navigate("/ajouter-ranch")}
          >
            <FaPlus />
            <span>{t.addRanch}</span>
          </button>
        </div>
      )}

      {/* Barre de recherche */}
      <div className="top-section">
        <SearchBar
          placeholder={t.searchByRanch}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="default"
          ariaLabel="Rechercher par nom, code, localisation"
        />
      </div>

      {/* DataGrid */}
      {ranches.length === 0 ? (
        <div className="empty-message-container">
          <p className="empty-message">Aucun ranch enregistré.</p>
        </div>
      ) : (
        <DataGridTable
          rows={ranches}
          columns={columns}
          loading={loading}
          storageKey="ranches-zoom-level"
          zoomLevel={zoomLevel}
          onZoomChange={setZoomLevel}
          initialSort={{ field: 'nom', sort: 'asc' }}
          pageSize={25}
          containerClassName="ranches-grid-container"
        />
      )}
    </div>
  );
}
